/**
 * Stage C — Guided Start: where does this Start's work happen?
 *
 * Asks the technician only what OptiBay cannot already know. It reads three
 * separate facts and never lets one stand in for another:
 *
 *   CURRENT PHYSICAL LOCATION   where the car IS        (vehicle_location_current)
 *   PLANNED LOCATION            where it should GO      (ticket_location_plan)
 *   PHYSICAL OCCUPANCY          which bays hold a car   (current truth only —
 *                               a planned bay occupies nothing)
 *
 * and decides one of:
 *
 *   IN_PLACE        the car is already in its bay (the planned one, or no bay
 *                   is planned) → the ordinary Start, no question
 *   BAY_MISMATCH    the car is in a bay but a DIFFERENT bay is planned → both
 *                   facts are shown and the technician decides; never silent
 *   MOVE_PLANNED    parked + a usable planned bay → preview + 5-second countdown,
 *                   then the ONE atomic move-and-start
 *   CHOOSE_BAY      parked, no usable planned bay → the technician picks
 *   NOT_ON_SITE     off site / not present → no pull-in is possible; say so
 *   LOCATION_NOT_RECORDED  no current-location row and the server would not
 *                   accept the legacy "parked" columns for this ticket → no
 *                   chooser the server would refuse (NOT_PARKED); say why
 *
 * The decision is a proposal. The server's pull_in_and_start_my_timer still
 * decides bay validity, occupancy, authorization and the Start itself, and a
 * refused bay is re-asked, never silently substituted.
 *
 * Pure: no React, no DOM, no Supabase. Every surface (desktop, tablet, phone)
 * uses this same decision.
 */
import type { CurrentVehicleLocation, VehicleLocationPlan } from '@/lib/location/model';
import { bayLabel, describeLocation, type LocationNames } from '@/lib/location/labels';
import { assistAnswer, type LocationAssist } from '@/lib/location/assist';

/** The default countdown before a guided move-and-start commits. */
export const GUIDED_START_COUNTDOWN_SECONDS = 5;

export interface GuidedStartBay {
  id: string;
  name: string | null;
  bayNumber: number | null;
  isActive: boolean | null;
  isLockedOut: boolean | null;
  category: string | null;
  techName: string | null;
}

export interface GuidedStartContext {
  /** This ticket's current physical truth (null: nothing recorded). */
  current: CurrentVehicleLocation | null;
  /** This ticket's planned location (null: no plan). */
  plan: VehicleLocationPlan | null;
  bays: readonly GuidedStartBay[];
  /** Bays a vehicle physically occupies right now (current truth). */
  occupiedBayIds: ReadonlySet<string>;
  /**
   * The legacy columns say "parked" (the card's pull-in rule). Used ONLY when
   * no physical truth is recorded AND `legacyFallback` holds.
   */
  legacyParked: boolean;
  /**
   * The server's pull-in reads this ticket's legacy columns when it has no
   * row of its own — only for a DEPENDENT ticket (rollover continuation,
   * specialty sub-ticket: vehicle_location_holder <> id). Any other ticket
   * without a row is refused NOT_PARKED, so it is never offered a chooser.
   */
  legacyFallback: boolean;
  /** Bays the server already refused in THIS act — never proposed again. */
  refusedBayIds: ReadonlySet<string>;
  /** "Judy Bloom’s 2003 Grand Prix" (or null when unknown). */
  vehicleLabel: string | null;
  /** parking spot id → label, for the "from" wording. */
  spotLabels: ReadonlyMap<string, string>;
  /**
   * #19 — the server's camera decision for this Start (absent / NONE: exactly
   * the behaviour without cameras). Never a prerequisite.
   */
  cameraAssist?: LocationAssist | null;
}

export type ChooseBayReason =
  | 'NO_PLAN'            // nothing planned
  | 'PLAN_NOT_A_BAY'     // the plan is a parking spot
  | 'PLAN_UNAVAILABLE'   // the planned bay is inactive, locked out, a parking bay, or gone
  | 'PLAN_OCCUPIED'      // another vehicle is physically in the planned bay
  | 'PLAN_REFUSED'       // the server just refused the planned bay
  | 'CHOICE_REFUSED';    // the server refused a bay chosen in this act — no countdown again

export type GuidedStartDecision =
  | { kind: 'IN_PLACE'; bayId: string | null }
  | { kind: 'MOVE_PLANNED'; fromLabel: string; whereNow: string; toBayId: string; toLabel: string; preview: string }
  /**
   * #19 — a fresh, exact, matched camera sighting puts this parked car in a
   * usable empty bay: the bay question is pre-answered ("Found it in Bay 3"),
   * the technician confirms with one tap or changes it. The commit is the
   * SAME pull-in (TECH_PULL_IN) as any chosen bay — the camera records nothing.
   */
  | { kind: 'CAMERA_FOUND'; fromLabel: string; whereNow: string; bayId: string; label: string }
  | {
      kind: 'CHOOSE_BAY';
      /** Reads after "from" ("Spot 12", "the lot", "somewhere on site"). */
      fromLabel: string;
      /** Where it is, as a sentence ("It's in Spot 12."). */
      whereNow: string;
      reason: ChooseBayReason;
      plannedBayId: string | null;
      plannedLabel: string | null;
    }
  | { kind: 'NOT_ON_SITE'; title: string; message: string }
  | { kind: 'LOCATION_NOT_RECORDED'; title: string; message: string }
  | {
      kind: 'BAY_MISMATCH';
      currentBayId: string;
      currentLabel: string;
      plannedBayId: string;
      plannedLabel: string;
      /** The planned bay's physical state — never proposed as valid when it is not. */
      plannedState: PlannedBayState;
    };

/**
 * AVAILABLE: usable and physically empty. OCCUPIED: another car is in it.
 * UNAVAILABLE: inactive, locked out, a parking bay, or gone.
 */
export type PlannedBayState = 'AVAILABLE' | 'OCCUPIED' | 'UNAVAILABLE';

const PARKED_KINDS = new Set(['PARKING_SPOT', 'ON_SITE_GENERAL', 'ON_SITE_UNKNOWN_EXACT']);

function names(ctx: GuidedStartContext): LocationNames {
  return {
    bays: new Map(ctx.bays.map((b) => [b.id, { name: b.name || `Bay ${b.bayNumber ?? ''}`.trim(), techName: b.techName }])),
    spots: new Map([...ctx.spotLabels].map(([id, label]) => [id, { label }])),
  };
}

/** A bay a vehicle can be pulled into at all (the server's own static rules). */
export function isUsableWorkBay(bay: GuidedStartBay | undefined): bay is GuidedStartBay {
  return !!bay && bay.isActive !== false && bay.isLockedOut !== true && bay.category !== 'parking';
}

/** Where the car is, in words — never more precise than the truth. */
export function whereWordsOf(ctx: GuidedStartContext) {
  // No row: only ever "parked, not recorded" — no invented spot.
  return describeLocation(ctx.current, names(ctx), { parkedNotRecorded: !ctx.current && ctx.legacyParked });
}

/** The "from" phrase: "Spot 12", "the lot", "where it's parked". */
export function fromLabelOf(ctx: GuidedStartContext): string {
  return whereWordsOf(ctx).from;
}

export function guidedStartPreview(vehicleLabel: string | null, fromLabel: string, toLabel: string): string {
  return `Moving ${vehicleLabel ?? 'this vehicle'} from ${fromLabel} to ${toLabel}`;
}

export function decideGuidedStart(ctx: GuidedStartContext): GuidedStartDecision {
  const cur = ctx.current;
  if (cur?.kind === 'BAY') {
    // "Already in a bay" is not "already in the correct bay". A different
    // planned bay is surfaced, never silently ignored. The move itself is not
    // offered here: pull_in_and_start_my_timer is for parked cars only, and a
    // standalone bay-to-bay move is not a technician's act
    // (can_relocate_vehicles_for_shop) — so there is no atomic path to fake.
    const plan = ctx.plan;
    if (plan?.kind !== 'BAY' || !plan.bayId || !cur.bayId || plan.bayId === cur.bayId) {
      return { kind: 'IN_PLACE', bayId: cur.bayId };
    }
    const n = names(ctx);
    const bay = ctx.bays.find((b) => b.id === plan.bayId);
    return {
      kind: 'BAY_MISMATCH',
      currentBayId: cur.bayId,
      currentLabel: bayLabel(cur.bayId, n),
      plannedBayId: plan.bayId,
      plannedLabel: bayLabel(plan.bayId, n),
      plannedState: !isUsableWorkBay(bay) ? 'UNAVAILABLE'
        : ctx.occupiedBayIds.has(plan.bayId) ? 'OCCUPIED' : 'AVAILABLE',
    };
  }
  if (cur?.kind === 'OFF_SITE') {
    return {
      kind: 'NOT_ON_SITE',
      title: "Off site — can't start yet",
      message: `${whereWordsOf(ctx).sentence} Bring it back and record where it is before starting work.`,
    };
  }
  if (cur?.kind === 'NOT_PRESENT') {
    return {
      kind: 'NOT_ON_SITE',
      title: 'Not at the shop',
      message: "It was recorded as having left the shop. Check it in before starting work.",
    };
  }
  if (!cur && ctx.legacyParked && !ctx.legacyFallback) {
    // The card thinks it is parked, but OptiBay has no current location for
    // it — and the pull-in would refuse it (NOT_PARKED). Never a chooser
    // that cannot succeed.
    return {
      kind: 'LOCATION_NOT_RECORDED',
      title: 'Record where it is first',
      message: "It's parked, but OptiBay hasn't recorded where. Record its location (or check it in) before starting work.",
    };
  }
  const parked = cur ? PARKED_KINDS.has(cur.kind) : ctx.legacyParked;
  if (!parked) return { kind: 'IN_PLACE', bayId: null };   // nothing to move: the server decides presence

  const n = names(ctx);
  const words = whereWordsOf(ctx);
  const fromLabel = words.from;
  const plan = ctx.plan;
  const choose = (reason: ChooseBayReason, plannedBayId: string | null = null): GuidedStartDecision => ({
    kind: 'CHOOSE_BAY',
    fromLabel,
    whereNow: words.sentence,
    reason,
    plannedBayId,
    plannedLabel: plannedBayId ? bayLabel(plannedBayId, n) : null,
  });

  // #19 — a camera answer beats a plan: it says where the car IS. Never after
  // a refusal in this act (the technician is choosing), never an occupied,
  // unusable or refused bay (assistAnswer), never without the server's
  // EXACT_CONFIDENT decision.
  const camera = ctx.refusedBayIds.size === 0 ? cameraFoundBay(ctx) : null;
  if (camera) return { kind: 'CAMERA_FOUND', fromLabel, whereNow: words.sentence, bayId: camera, label: bayLabel(camera, n) };

  if (!plan) return choose('NO_PLAN');
  if (plan.kind !== 'BAY' || !plan.bayId) return choose('PLAN_NOT_A_BAY');
  const bay = ctx.bays.find((b) => b.id === plan.bayId);
  if (ctx.refusedBayIds.has(plan.bayId)) return choose('PLAN_REFUSED', plan.bayId);
  if (!isUsableWorkBay(bay)) return choose('PLAN_UNAVAILABLE', plan.bayId);
  if (ctx.occupiedBayIds.has(plan.bayId)) return choose('PLAN_OCCUPIED', plan.bayId);
  // Item #14 — after ANY refusal in this act the technician is choosing; the
  // countdown never restarts (not even toward a planned bay still free).
  if (ctx.refusedBayIds.size > 0) return choose('CHOICE_REFUSED', plan.bayId);

  const toLabel = bayLabel(plan.bayId, n);
  return {
    kind: 'MOVE_PLANNED',
    fromLabel,
    whereNow: words.sentence,
    toBayId: plan.bayId,
    toLabel,
    preview: guidedStartPreview(ctx.vehicleLabel, fromLabel, toLabel),
  };
}

export interface GuidedBayChoice {
  id: string;
  label: string;
  occupied: boolean;
  refused: boolean;
  planned: boolean;
  /** #19 — the camera suggests this bay ("Looks like"). Highlighted, never preselected. */
  suggested?: true;
}

/** The usable bays a camera answer must land on (occupied / refused excluded by assistAnswer). */
function cameraChoices(ctx: GuidedStartContext) {
  return ctx.bays.filter((b) => isUsableWorkBay(b))
    .map((b) => ({ id: b.id, occupied: ctx.occupiedBayIds.has(b.id), refused: ctx.refusedBayIds.has(b.id) }));
}
function cameraFoundBay(ctx: GuidedStartContext): string | null {
  return assistAnswer(ctx.cameraAssist, 'BAY', cameraChoices(ctx)).found?.id ?? null;
}
/** #19 — the bay the camera suggests (medium confidence), if it is a real choice. */
export function cameraSuggestedBay(ctx: GuidedStartContext): string | null {
  return assistAnswer(ctx.cameraAssist, 'BAY', cameraChoices(ctx)).suggested?.id ?? null;
}

/**
 * The bays a technician may choose: usable work bays, physically occupied
 * ones shown (and disabled), a bay refused in this act marked as such.
 * Nothing is preselected — the technician chooses (Stage 0D).
 */
export function guidedBayChoices(ctx: GuidedStartContext): GuidedBayChoice[] {
  const n = names(ctx);
  const suggested = cameraSuggestedBay(ctx);
  return ctx.bays
    .filter((b) => isUsableWorkBay(b))
    .map((b) => ({
      id: b.id,
      label: bayLabel(b.id, n),
      occupied: ctx.occupiedBayIds.has(b.id),
      refused: ctx.refusedBayIds.has(b.id),
      planned: ctx.plan?.kind === 'BAY' && ctx.plan.bayId === b.id,
      ...(suggested === b.id ? { suggested: true as const } : {}),
    }));
}

/** Why the technician is being asked, in one truthful sentence. */
export function chooseBayMessage(d: Extract<GuidedStartDecision, { kind: 'CHOOSE_BAY' }>): string {
  switch (d.reason) {
    case 'NO_PLAN': return `${d.whereNow} Choose the bay to bring it into.`;
    case 'PLAN_NOT_A_BAY': return `${d.whereNow} Choose the bay to bring it into.`;
    case 'PLAN_UNAVAILABLE': return `${d.plannedLabel} can't take a vehicle right now. Choose another bay.`;
    case 'PLAN_OCCUPIED': return `Another vehicle is in ${d.plannedLabel}. Choose another bay.`;
    case 'PLAN_REFUSED': return `${d.plannedLabel} could not take the vehicle. Nothing moved — choose another bay.`;
    case 'CHOICE_REFUSED': return 'That bay could not take the vehicle. Nothing moved — choose another bay.';
    default: {
      const never: never = d.reason;
      return String(never);
    }
  }
}

/** The mismatch, in truthful words: where the car is, where it is planned. */
export function bayMismatchMessage(
  d: Extract<GuidedStartDecision, { kind: 'BAY_MISMATCH' }>,
  vehicleLabel: string | null,
): string {
  const lead = `${vehicleLabel ?? 'This vehicle'} is in ${d.currentLabel}, but it is planned for ${d.plannedLabel}.`;
  switch (d.plannedState) {
    case 'AVAILABLE': return lead;
    case 'OCCUPIED': return `${lead} Another vehicle is in ${d.plannedLabel}.`;
    case 'UNAVAILABLE': return `${lead} ${d.plannedLabel} can't take a vehicle right now.`;
    default: {
      const never: never = d.plannedState;
      return String(never);
    }
  }
}

/**
 * A single-fire countdown. `tick` is driven by the caller's clock (a React
 * interval, a test loop); `fire` runs at most once however it is reached
 * (zero, "Start now", or both at once), and never after `cancel`.
 */
export class GuidedCountdown {
  private remaining: number;
  private done = false;
  constructor(seconds: number, private readonly fire: () => void) {
    this.remaining = seconds;
  }
  get secondsLeft(): number { return this.remaining; }
  get finished(): boolean { return this.done; }
  /** One second passed. Fires at zero. */
  tick(): number {
    if (this.done) return this.remaining;
    this.remaining = Math.max(0, this.remaining - 1);
    if (this.remaining === 0) this.now();
    return this.remaining;
  }
  /** Commit immediately (the "Start now" button). */
  now(): void {
    if (this.done) return;
    this.done = true;
    this.remaining = 0;
    this.fire();
  }
  /** Cancel: nothing will fire. */
  cancel(): void {
    this.done = true;
  }
}

/**
 * Oct 20 (D-4) — the guided RESUME of paused work whose vehicle is parked: always an explicit bay choice (never a
 * countdown — resuming someone's paused work is a deliberate act). The planned bay, when usable, is offered as
 * "Planned"; a bay the server just refused in this act is marked and not selectable (guidedBayChoices).
 */
export function resumeBayChoiceDecision(ctx: GuidedStartContext): Extract<GuidedStartDecision, { kind: 'CHOOSE_BAY' }> {
  const d = decideGuidedStart({ ...ctx, cameraAssist: undefined });
  const where = whereWordsOf(ctx);
  if (d.kind === 'CHOOSE_BAY') return d;
  return {
    kind: 'CHOOSE_BAY',
    fromLabel: where.from,
    whereNow: where.sentence,
    reason: 'NO_PLAN',
    plannedBayId: d.kind === 'MOVE_PLANNED' ? d.toBayId : null,
    plannedLabel: d.kind === 'MOVE_PLANNED' ? d.toLabel : null,
  };
}
